import LogRocket from 'logrocket';
import type { ReactNode } from 'react';
import {
    useMemo,
    createContext,
    useContext,
    useCallback,
    useState,
    useEffect,
} from 'react';
import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQueryClient } from 'react-query';

import { useCookie } from '../hooks/useCookie';
import type { IUserProfile, IUserSignInData, IUser } from '../types/user';

interface IAuthContext<TUserProfile = IUserProfile> {
    accessToken: string | null;
    refreshToken: string | null;
    user: IUserProfile | null;
    handleUserSignIn: (data: IUserSignInData) => void;
    handleUserSignOff: () => void;
    isUserFetching: boolean;
    storeSignedUser: (data: IStoreSignedUserProps<TUserProfile>) => void;
    signInPath: string;
}

interface IStoreSignedUserProps<TUserProfile = IUserProfile> {
    accessToken: string;
    refreshToken: string;
    user: TUserProfile;
}

const AuthContext = createContext<IAuthContext | null>(null);

const useAuthContext = () => {
    const contextValue = useContext(AuthContext);

    if (!contextValue) {
        throw new Error(
            'useAuthContext must be used within AuthContextProvider scope'
        );
    }

    return contextValue;
};

interface ICookieNames {
    accessToken: string;
    refreshToken: string;
}

interface IAuthContextProviderProps<
    TUserProfile extends IUserProfile,
    TUserSignInData extends IUserSignInData,
    TRequestException
> {
    // application name used to id logrocket
    logRocketId?: string;
    cookies: ICookieNames;
    signInUser: (data: TUserSignInData) => Promise<IUser<TUserProfile>>;
    signInPath: string;
    useGetUserQuery: (
        props: UseQueryOptions<TUserProfile, TRequestException>
    ) => UseQueryResult<TUserProfile, TRequestException>;
    children?: ReactNode | undefined;
}

function AuthContextProvider<
    TUserProfile extends IUserProfile,
    TUserSignInData extends IUserSignInData,
    TRequestException
>({
    children,
    cookies,
    signInUser,
    useGetUserQuery,
    logRocketId,
    signInPath,
}: IAuthContextProviderProps<
    TUserProfile,
    TUserSignInData,
    TRequestException
>) {
    const [accessToken, setAccessToken, deleteAccessToken] = useCookie(
        cookies.accessToken
    );
    const [refreshToken, setRefreshToken, deleteRefreshToken] = useCookie(
        cookies.refreshToken
    );

    const [userProfile, setUserProfile] = useState<TUserProfile | null>(null);

    const queryClient = useQueryClient();

    /* Get user profile data */
    const { isFetching, isLoading } = useGetUserQuery({
        onSuccess: (response) => setUserProfile(response),
    });

    const [isLogRocketInitialized, setIsLogRocketInitialized] = useState(false);
    const [isLogRocketUserInitialized, setIsLogRocketUserInitialized] =
        useState(false);

    useEffect(() => {
        // eslint-disable-next-line node/no-process-env
        // @ts-ignore
        if (process.env.NODE_ENV !== 'production') return;

        if (logRocketId) {
            LogRocket.init(logRocketId);
            setIsLogRocketInitialized(true);
        }
    }, []);

    useEffect(() => {
        // eslint-disable-next-line node/no-process-env
        // @ts-ignore
        if (process.env.NODE_ENV !== 'production') return;

        if (!isLogRocketUserInitialized && userProfile) {
            LogRocket.identify(userProfile.uid, {
                email: userProfile.email,
                name: `${userProfile.firstName} ${userProfile.lastName}`,
                disabled: userProfile?.disabled ?? false,
                username: userProfile?.username ?? '',
                logRocketId: logRocketId ?? '',
            });

            setIsLogRocketUserInitialized(true);
        }
    }, [userProfile, isLogRocketInitialized]);

    /* User sign in */
    const handleUserSignIn = useCallback(
        async (formData) => {
            const response = await signInUser(formData);
            setAccessToken(response.accessToken);
            setRefreshToken(response.refreshToken);
            setUserProfile(response.user);
        },
        [setAccessToken, setRefreshToken]
    );

    /* User sign off */
    const handleUserSignOff = useCallback(async () => {
        await queryClient.invalidateQueries();
        deleteAccessToken();
        deleteRefreshToken();
        setUserProfile(null);
        queryClient.clear();
    }, [deleteAccessToken, deleteRefreshToken, queryClient]);

    /* Store data about signed user */
    const storeSignedUser = useCallback(
        (data: IStoreSignedUserProps<TUserProfile>) => {
            setAccessToken(data.accessToken);
            setRefreshToken(data.refreshToken);
            setUserProfile(data.user);
        },
        [setAccessToken, setRefreshToken, setUserProfile]
    );

    const contextValue: IAuthContext<TUserProfile> = useMemo(() => {
        return {
            accessToken,
            refreshToken,
            storeSignedUser,
            user: userProfile,
            isUserFetching: isFetching,
            handleUserSignIn,
            handleUserSignOff,
            isLoading,
            signInPath,
        };
    }, [
        handleUserSignIn,
        handleUserSignOff,
        userProfile,
        accessToken,
        refreshToken,
        isFetching,
        storeSignedUser,
        isLoading,
        signInPath,
    ]);

    return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <AuthContext.Provider value={contextValue as any}>
            {children}
        </AuthContext.Provider>
    );
}

export { AuthContext, useAuthContext, AuthContextProvider };
